<template>
  <div class="certs-page">

    <div class="page-header">
      <div>
        <h5 class="page-title">My Certificates</h5>
        <p class="page-sub">Certificates you have earned from completed training courses</p>
      </div>
      <div v-if="certificates.length" style="display:flex;gap:8px;">
        <button class="btn-export" @click="openExportModal('csv')" :disabled="exportLoading">
          <font-awesome-icon :icon="exportLoading && pendingExportFmt==='csv' ? 'spinner' : 'file-csv'" :spin="exportLoading && pendingExportFmt==='csv'" /> CSV
        </button>
        <button class="btn-export" @click="openExportModal('pdf')" :disabled="exportLoading">
          <font-awesome-icon :icon="exportLoading && pendingExportFmt==='pdf' ? 'spinner' : 'file-pdf'" :spin="exportLoading && pendingExportFmt==='pdf'" /> PDF
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="full-loader"><div class="spinner"></div></div>

    <!-- Empty -->
    <div v-else-if="!certificates.length" class="empty-state">
      <font-awesome-icon icon="certificate" class="empty-icon" />
      <p class="empty-text">No certificates yet. Complete a training course to earn your first certificate.</p>
      <button class="btn-outline" @click="$router.push($url('/hr/training/my-training'))">
        <font-awesome-icon icon="graduation-cap" /> Go to My Training
      </button>
    </div>

    <!-- Grid -->
    <div v-else class="cert-grid">
      <div v-for="cert in certificates" :key="cert.id" class="cert-card">

        <div class="cert-ribbon" :class="'cert-ribbon--' + cert.status"></div>

        <div class="cert-body">
          <div class="cert-icon-wrap">
            <font-awesome-icon icon="certificate" class="cert-icon" />
          </div>

          <div class="cert-info">
            <div class="cert-course">{{ cert.enrolment?.course?.title ?? '—' }}</div>
            <div class="cert-no">{{ cert.certificate_no }}</div>
          </div>

          <span :class="['cert-status-badge', 'cert-status--' + cert.status]">
            {{ statusLabel(cert.status) }}
          </span>
        </div>

        <div class="cert-dates">
          <div class="cert-date-item">
            <span class="cert-date-label">Issued</span>
            <span class="cert-date-val">{{ formatDate(cert.issued_at) }}</span>
          </div>
          <div v-if="cert.expires_at" class="cert-date-item">
            <span class="cert-date-label">Expires</span>
            <span :class="['cert-date-val', isExpiringSoon(cert) ? 'cert-date-val--warn' : '']">
              {{ formatDate(cert.expires_at) }}
              <span v-if="isExpiringSoon(cert)" class="expiry-note">Expiring soon</span>
            </span>
          </div>
          <div v-else class="cert-date-item">
            <span class="cert-date-label">Expires</span>
            <span class="cert-date-val muted">No expiry</span>
          </div>
        </div>

        <div class="cert-actions">
          <button
            class="btn-download"
            :disabled="downloading === cert.id"
            @click="downloadCertificate(cert)"
          >
            <span v-if="downloading === cert.id" class="spinner-xs"></span>
            <font-awesome-icon v-else icon="download" />
            {{ downloading === cert.id ? 'Preparing…' : 'Download PDF' }}
          </button>
          <a
            v-if="cert.verification_token"
            :href="verifyUrl(cert)"
            target="_blank"
            rel="noopener"
            class="btn-verify"
          >
            <font-awesome-icon icon="shield-alt" /> Verify
          </a>
          <button class="btn-view" @click="$router.push($url('/hr/training/player/' + cert.enrolment_id))">
            <font-awesome-icon icon="play-circle" /> View Course
          </button>
        </div>

      </div>
    </div>

    <!-- ── Export Column Picker ── -->
    <transition name="fade">
      <div v-if="showExportModal" class="col-picker-backdrop" @click.self="showExportModal = false">
        <div class="col-picker-modal">
          <div class="col-picker-header">
            <div>
              <h3 class="col-picker-title">Select Columns</h3>
              <p class="col-picker-sub">Choose what to include in your {{ pendingExportFmt.toUpperCase() }} export</p>
            </div>
            <button class="col-picker-close" @click="showExportModal = false"><font-awesome-icon icon="times" /></button>
          </div>
          <div class="col-picker-actions">
            <button class="col-picker-link" @click="selectedExportCols = exportColumnDefs.map(c => c.key)">Select all</button>
            <span class="col-picker-sep">·</span>
            <button class="col-picker-link" @click="selectedExportCols = []">Deselect all</button>
            <span class="col-picker-count">{{ selectedExportCols.length }} of {{ exportColumnDefs.length }} selected</span>
          </div>
          <div class="col-picker-grid">
            <label v-for="col in exportColumnDefs" :key="col.key" class="col-picker-item">
              <input type="checkbox" :value="col.key" v-model="selectedExportCols" class="col-picker-check" />
              <span class="col-picker-label">{{ col.label }}</span>
            </label>
          </div>
          <div class="col-picker-footer">
            <button class="btn-outline" @click="showExportModal = false">Cancel</button>
            <button class="btn-dl" :disabled="selectedExportCols.length === 0 || exportLoading" @click="doExport()">
              <font-awesome-icon :icon="exportLoading ? 'spinner' : pendingExportFmt === 'pdf' ? 'file-pdf' : 'file-csv'" :spin="exportLoading" />
              Export {{ pendingExportFmt.toUpperCase() }}
            </button>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

export default {
  name: 'TrainingCertificates',
  setup() { return { toast: useToast() }; },
  data() {
    return {
      loading: true,
      certificates: [],
      downloading: null,
      showExportModal: false,
      pendingExportFmt: 'csv',
      selectedExportCols: [],
      exportLoading: false,
      exportColumnDefs: [
        { key: 'cert_no', label: 'Cert No', pdfWidth: 120 },
        { key: 'course',  label: 'Course',  pdfWidth: 281 },
        { key: 'status',  label: 'Status',  pdfWidth: 80 },
        { key: 'issued',  label: 'Issued',  pdfWidth: 140 },
        { key: 'expires', label: 'Expires', pdfWidth: 140 },
      ],
    };
  },
  async created() {
    await this.loadCertificates();
  },
  methods: {
    openExportModal(format) {
      this.pendingExportFmt   = format;
      this.selectedExportCols = this.exportColumnDefs.map(c => c.key);
      this.showExportModal    = true;
    },
    getRowValue(row, key) {
      switch (key) {
        case 'cert_no': return row.certificate_no || '—';
        case 'course':  return row.enrolment?.course?.title || '—';
        case 'status':  return this.statusLabel(row.status);
        case 'issued':  return this.formatDate(row.issued_at);
        case 'expires': return row.expires_at ? this.formatDate(row.expires_at) : 'No expiry';
        default:        return '—';
      }
    },
    async doExport() {
      if (!this.selectedExportCols.length) return;
      this.exportLoading   = true;
      this.showExportModal = false;
      try {
        const rows    = this.certificates;
        const cols    = this.exportColumnDefs.filter(c => this.selectedExportCols.includes(c.key));
        const headers = cols.map(c => c.label);
        const body    = rows.map(row => cols.map(c => this.getRowValue(row, c.key)));
        const date    = new Date().toISOString().slice(0, 10);
        if (this.pendingExportFmt === 'csv') {
          const csv  = [headers, ...body].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
          const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
          const url  = URL.createObjectURL(blob);
          const a    = document.createElement('a');
          a.href = url; a.download = `my-certificates-${date}.csv`; a.click();
          URL.revokeObjectURL(url);
        } else {
          const doc       = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
          const usable    = 761;
          const totalW    = cols.reduce((s, c) => s + c.pdfWidth, 0);
          const scale     = usable / totalW;
          const colStyles = {};
          cols.forEach((c, i) => { colStyles[i] = { cellWidth: c.pdfWidth * scale }; });
          doc.setFontSize(18); doc.setFont('helvetica', 'bold'); doc.setTextColor(26, 43, 69);
          doc.text('My Certificates', 40, 46);
          doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(122, 144, 176);
          doc.text(`Exported ${new Date().toLocaleDateString('en-GB')}   ·   ${rows.length} record(s)`, 40, 62);
          doc.autoTable({
            startY: 78, head: [headers], body,
            styles: { fontSize: 7.5, cellPadding: 3.5, textColor: [26, 43, 69], overflow: 'ellipsize' },
            headStyles: { fillColor: [0, 74, 173], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            columnStyles: colStyles, tableWidth: usable, margin: { left: 40, right: 40 },
            didDrawPage(data) {
              doc.setFontSize(8); doc.setTextColor(192, 202, 214);
              doc.text(`Page ${data.pageNumber}`, doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 16, { align: 'center' });
            },
          });
          doc.save(`my-certificates-${date}.pdf`);
        }
      } catch { this.toast.error('Export failed. Please try again.'); }
      finally   { this.exportLoading = false; }
    },

    async loadCertificates() {
      this.loading = true;
      try {
        const { data } = await axios.get('/training/me/certificates');
        this.certificates = data.data ?? [];
      } catch {
        this.toast.error('Failed to load certificates.');
      } finally {
        this.loading = false;
      }
    },

    formatDate(d) {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    isExpiringSoon(cert) {
      if (!cert.expires_at || cert.status !== 'valid') return false;
      const days = Math.ceil((new Date(cert.expires_at) - new Date()) / 86400000);
      return days >= 0 && days <= 30;
    },

    statusLabel(status) {
      return { valid: 'Valid', expired: 'Expired', revoked: 'Revoked' }[status] ?? status;
    },

    async downloadCertificate(cert) {
      this.downloading = cert.id;
      try {
        const { data } = await axios.get(`/training/me/certificates/${cert.id}/download`, {
          responseType: 'blob',
        });
        const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
        const a   = document.createElement('a');
        a.href     = url;
        a.download = `certificate-${cert.certificate_no}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch {
        this.toast.error('Failed to download certificate. Please try again.');
      } finally {
        this.downloading = null;
      }
    },

    verifyUrl(cert) {
      const code = this.$route?.params?.companyCode || localStorage.getItem('companyCode') || '';
      const base = window.location.origin;
      return `${base}/${code}/certificates/verify/${cert.verification_token}`;
    },
  },
};
</script>

<style scoped>
.certs-page { padding-bottom: 40px; }

.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom: 24px; }
.page-title  { font-size: 18px; font-weight: 700; color: #1a2b45; margin: 0 0 4px; }
.page-sub    { font-size: 13px; color: #7a90b0; margin: 0; }
.btn-export { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#3d5068; font-size:13px; font-weight:600; padding:8px 14px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; white-space:nowrap; font-family:inherit; }
.btn-export:hover:not(:disabled) { background:#f4f7fb; border-color:#c8d6e8; }
.btn-export:disabled { opacity:.45; cursor:not-allowed; }
.btn-dl { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-dl:hover:not(:disabled) { background:#0056d2; }
.btn-dl:disabled { opacity:.45; cursor:not-allowed; }

/* -- Column picker ------------------------------------------------- */
.col-picker-backdrop { position:fixed; inset:0; background:rgba(15,25,40,.45); z-index:1050; display:flex; align-items:center; justify-content:center; padding:20px; }
.col-picker-modal    { background:#fff; border-radius:14px; width:100%; max-width:480px; box-shadow:0 24px 60px rgba(0,0,0,.18); overflow:hidden; }
.col-picker-header   { display:flex; align-items:flex-start; justify-content:space-between; padding:20px 22px 14px; border-bottom:1px solid #f0f4f9; }
.col-picker-title    { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.col-picker-sub      { font-size:12px; color:#7a90b0; margin:0; }
.col-picker-close    { background:none; border:none; color:#7a90b0; font-size:15px; cursor:pointer; padding:4px; line-height:1; }
.col-picker-close:hover { color:#1a2b45; }
.col-picker-actions  { display:flex; align-items:center; gap:8px; padding:10px 22px; background:#f8fafc; font-size:12px; border-bottom:1px solid #f0f4f9; }
.col-picker-link     { background:none; border:none; color:#004AAD; font-size:12px; cursor:pointer; padding:0; font-weight:600; font-family:inherit; }
.col-picker-link:hover { text-decoration:underline; }
.col-picker-sep      { color:#c5d3e0; }
.col-picker-count    { margin-left:auto; color:#7a90b0; font-size:11.5px; }
.col-picker-grid     { display:grid; grid-template-columns:1fr 1fr; gap:2px; padding:14px 16px; max-height:280px; overflow-y:auto; }
.col-picker-item     { display:flex; align-items:center; gap:9px; padding:9px 10px; border-radius:8px; cursor:pointer; transition:background .1s; }
.col-picker-item:hover { background:#f4f7fb; }
.col-picker-check    { width:15px; height:15px; accent-color:#004AAD; cursor:pointer; flex-shrink:0; }
.col-picker-label    { font-size:13px; color:#1a2b45; user-select:none; }
.col-picker-footer   { display:flex; justify-content:flex-end; gap:8px; padding:14px 20px; border-top:1px solid #f0f4f9; }
.fade-enter-active, .fade-leave-active { transition:opacity .15s ease; }
.fade-enter, .fade-leave-to { opacity:0; }

/* Loading */
.full-loader { display: flex; justify-content: center; padding: 60px 0; }
.spinner { width: 32px; height: 32px; border: 3px solid #e4eaf2; border-top-color: #004AAD; border-radius: 50%; animation: spin .7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* Empty */
.empty-state { display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 80px 20px; text-align: center; }
.empty-icon  { font-size: 52px; color: #d0dbe8; }
.empty-text  { font-size: 14px; color: #7a90b0; max-width: 360px; margin: 0; }

/* Grid */
.cert-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 20px; }

/* Card */
.cert-card { background: #fff; border-radius: 14px; border: 1px solid #e4eaf2; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,.04); display: flex; flex-direction: column; }

.cert-ribbon { height: 4px; }
.cert-ribbon--valid   { background: #2d8a3e; }
.cert-ribbon--expired { background: #b0bdd0; }
.cert-ribbon--revoked { background: #e03535; }

.cert-body { display: flex; align-items: flex-start; gap: 14px; padding: 18px 18px 12px; }
.cert-icon-wrap { width: 44px; height: 44px; border-radius: 12px; background: #fef8e7; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.cert-icon { font-size: 22px; color: #e07000; }
.cert-info { flex: 1; min-width: 0; }
.cert-course { font-size: 14px; font-weight: 700; color: #1a2b45; margin-bottom: 3px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cert-no { font-size: 12px; color: #7a90b0; font-family: monospace; }

.cert-status-badge { padding: 3px 9px; border-radius: 10px; font-size: 11px; font-weight: 700; flex-shrink: 0; }
.cert-status--valid   { background: #e8f5e9; color: #2e7d32; }
.cert-status--expired { background: #f5f5f5; color: #7a90b0; }
.cert-status--revoked { background: #fff0f0; color: #c03030; }

.cert-dates { display: flex; gap: 24px; padding: 0 18px 14px; border-bottom: 1px solid #f0f4f8; }
.cert-date-item { display: flex; flex-direction: column; gap: 2px; }
.cert-date-label { font-size: 10px; font-weight: 700; color: #a0b0c8; text-transform: uppercase; letter-spacing: .04em; }
.cert-date-val { font-size: 13px; font-weight: 600; color: #1a2b45; }
.cert-date-val--warn { color: #c07000; }
.expiry-note { margin-left: 6px; font-size: 10px; font-weight: 700; background: #fff3e0; color: #c07000; padding: 1px 6px; border-radius: 8px; }

.cert-actions { display: flex; gap: 8px; padding: 12px 18px; flex-wrap: wrap; }
.btn-download { display: inline-flex; align-items: center; gap: 6px; background: #004AAD; color: #fff; border: none; font-size: 12px; font-weight: 600; padding: 7px 14px; border-radius: 8px; cursor: pointer; }
.btn-download:hover:not(:disabled) { background: #0056d2; }
.btn-download:disabled { opacity: .65; cursor: not-allowed; }
.btn-verify { display: inline-flex; align-items: center; gap: 6px; background: #eef4ff; color: #004AAD; border: none; font-size: 12px; font-weight: 600; padding: 7px 14px; border-radius: 8px; cursor: pointer; text-decoration: none; }
.btn-verify:hover { background: #ddeaff; }
.btn-view { display: inline-flex; align-items: center; gap: 6px; background: #f4f7fb; color: #1a2b45; border: none; font-size: 12px; font-weight: 600; padding: 7px 14px; border-radius: 8px; cursor: pointer; }
.btn-view:hover { background: #e8edf5; }

.spinner-xs { width: 12px; height: 12px; border: 2px solid rgba(255,255,255,.4); border-top-color: #fff; border-radius: 50%; animation: spin .7s linear infinite; display: inline-block; }
@keyframes spin { to { transform: rotate(360deg); } }

.btn-outline { display: inline-flex; align-items: center; gap: 7px; background: #fff; color: #1a2b45; border: 1.5px solid #dde5f0; font-size: 13px; font-weight: 500; padding: 9px 18px; border-radius: 8px; cursor: pointer; }
.btn-outline:hover { background: #f4f7fb; }
.muted { color: #a0b0c8; }
</style>
